import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  DatePicker,
  Descriptions,
  Select,
  Space,
  Table,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { apiClient } from '../../api/client';
import { getApiBaseUrl, getTrackingApiBaseUrl } from '../../config/env';
import { formatDateTime } from '../../utils/format';

interface HistoryPage<T> {
  data: T[];
  has_more: boolean;
}

interface UserActivity {
  id: string;
  name: string;
  received_at: string;
  value: Record<string, unknown>;
}

interface UserDevice {
  id: string;
  device_id: string;
  device_name: string;
  device_os: string;
  device_platform: string;
  client_version: string;
  client_build: string;
  user_agent: string;
  ip: string;
  country: string;
  first_seen_at: string;
  last_seen_at: string;
}

const emptyValue = (value: string) => value || '—';
const countryName = (code: string) => {
  if (!code) return 'Unknown';

  try {
    const name = new Intl.DisplayNames(['en'], { type: 'region' }).of(code);
    return name && name !== code ? `${name} (${code})` : code;
  } catch {
    return code;
  }
};

const HistoryPagination = ({
  page,
  perPage,
  hasMore,
  loading,
  onPage,
  onSize,
}: {
  page: number;
  perPage: number;
  hasMore: boolean;
  loading: boolean;
  onPage: (page: number) => void;
  onSize: (size: number) => void;
}) => (
  <Space wrap className="mt-4">
    <Button disabled={page === 1 || loading} onClick={() => onPage(page - 1)}>
      Previous
    </Button>
    <Typography.Text>Page {page}</Typography.Text>
    <Button disabled={!hasMore || loading} onClick={() => onPage(page + 1)}>
      Next
    </Button>
    <Select
      aria-label="Rows per page"
      value={perPage}
      options={[20, 50, 100].map((value) => ({
        value,
        label: `${value} / page`,
      }))}
      onChange={onSize}
    />
  </Space>
);

export const ActivitiesTab = ({ userID }: { userID: string }) => {
  const [range, setRange] = useState(() => {
    const to = dayjs();
    return { from: to.subtract(1, 'day'), to };
  });
  const [presetMinutes, setPresetMinutes] = useState<number | undefined>(1440);
  const [rangeError, setRangeError] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const baseURL = getTrackingApiBaseUrl();
  const query = useQuery({
    queryKey: [
      'user-activities',
      baseURL,
      userID,
      range.from.toISOString(),
      range.to.toISOString(),
      page,
      perPage,
    ],
    queryFn: async ({ signal }) => {
      const response = await apiClient.get<HistoryPage<UserActivity>>(
        `/api/v1/admin/users/${encodeURIComponent(userID)}/activities`,
        {
          baseURL,
          signal,
          withCredentials: false,
          timeout: 30000,
          params: {
            from: range.from.toISOString(),
            to: range.to.toISOString(),
            page,
            per_page: perPage,
          },
        },
      );
      return response.data;
    },
  });
  const columns: ColumnsType<UserActivity> = [
    {
      title: 'Received',
      dataIndex: 'received_at',
      render: formatDateTime,
      width: 210,
    },
    { title: 'Activity', dataIndex: 'name' },
    { title: 'Event ID', dataIndex: 'id', ellipsis: true },
  ];

  return (
    <Space direction="vertical" size={16} className="w-full">
      <Space wrap>
        <Select
          aria-label="Activity time window preset"
          placeholder="Custom range"
          value={presetMinutes}
          style={{ width: 170 }}
          options={[
            { value: 15, label: 'Last 15 minutes' },
            { value: 60, label: 'Last hour' },
            { value: 360, label: 'Last 6 hours' },
            { value: 720, label: 'Last 12 hours' },
            { value: 1440, label: 'Last 24 hours' },
            { value: 4320, label: 'Last 3 days' },
            { value: 10080, label: 'Last 7 days' },
            { value: 43200, label: 'Last 30 days' },
          ]}
          onChange={(minutes) => {
            const to = dayjs();
            setPresetMinutes(minutes);
            setRange({ from: to.subtract(minutes, 'minute'), to });
            setPage(1);
            setRangeError('');
          }}
        />
        <DatePicker.RangePicker
          showTime
          allowClear={false}
          value={[range.from, range.to]}
          aria-label="Activity time window"
          onChange={(values) => {
            if (!values?.[0] || !values[1]) return;
            if (
              values[1].diff(values[0], 'millisecond') <= 0 ||
              values[1].diff(values[0], 'millisecond') >
                31 * 24 * 60 * 60 * 1000
            ) {
              setRangeError(
                'Select a positive time window of at most 31 days.',
              );
              return;
            }
            setRangeError('');
            setPresetMinutes(undefined);
            setRange({ from: values[0], to: values[1] });
            setPage(1);
          }}
        />
        <Button
          onClick={() => {
            const to = dayjs();
            setRange({ from: to.subtract(1, 'day'), to });
            setPresetMinutes(1440);
            setPage(1);
            setRangeError('');
          }}
        >
          Last 24 hours
        </Button>
        <Button loading={query.isFetching} onClick={() => query.refetch()}>
          Refresh activities
        </Button>
      </Space>
      {rangeError && <Alert type="warning" showIcon message={rangeError} />}
      <Typography.Text type="secondary">
        Local time · Filtered by receipt time · Maximum window: 31 days. Expand
        an activity to view its payload.
      </Typography.Text>
      {query.error ? (
        <Alert
          type="error"
          showIcon
          message="Failed to load activities"
          description={
            query.error instanceof Error
              ? query.error.message
              : 'Request failed'
          }
          action={<Button onClick={() => query.refetch()}>Retry</Button>}
        />
      ) : (
        <>
          <Table<UserActivity>
            rowKey="id"
            columns={columns}
            dataSource={query.data?.data || []}
            loading={query.isFetching}
            pagination={false}
            scroll={{ x: 700 }}
            locale={{ emptyText: 'No activities in this time window' }}
            expandable={{
              expandedRowRender: (row) => (
                <pre
                  className="whitespace-pre-wrap break-words"
                  style={{ maxHeight: 400, overflow: 'auto' }}
                >
                  {JSON.stringify(row.value, null, 2)}
                </pre>
              ),
            }}
          />
          <HistoryPagination
            page={page}
            perPage={perPage}
            hasMore={query.data?.has_more || false}
            loading={query.isFetching}
            onPage={setPage}
            onSize={(size) => {
              setPerPage(size);
              setPage(1);
            }}
          />
        </>
      )}
    </Space>
  );
};

export const DevicesTab = ({ userID }: { userID: string }) => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const baseURL = getApiBaseUrl();
  const query = useQuery({
    queryKey: ['user-devices', baseURL, userID, page, perPage],
    queryFn: async ({ signal }) => {
      const response = await apiClient.get<HistoryPage<UserDevice>>(
        `/api/v1/admin/users/${encodeURIComponent(userID)}/devices`,
        {
          signal,
          params: { page, per_page: perPage },
        },
      );
      return response.data;
    },
  });
  const columns: ColumnsType<UserDevice> = [
    { title: 'Device', dataIndex: 'device_name', render: emptyValue },
    { title: 'Platform', dataIndex: 'device_platform', render: emptyValue },
    { title: 'OS', dataIndex: 'device_os', render: emptyValue },
    { title: 'Latest IP', dataIndex: 'ip', render: emptyValue },
    { title: 'Country', dataIndex: 'country', render: countryName },
    { title: 'First seen', dataIndex: 'first_seen_at', render: formatDateTime },
    { title: 'Last seen', dataIndex: 'last_seen_at', render: formatDateTime },
  ];

  return (
    <Space direction="vertical" size={16} className="w-full">
      <Space wrap>
        <Button loading={query.isFetching} onClick={() => query.refetch()}>
          Refresh devices
        </Button>
      </Space>

      {query.error ? (
        <Alert
          type="error"
          showIcon
          message="Failed to load devices"
          description={
            query.error instanceof Error
              ? query.error.message
              : 'Request failed'
          }
          action={<Button onClick={() => query.refetch()}>Retry</Button>}
        />
      ) : (
        <>
          <Table<UserDevice>
            rowKey="id"
            columns={columns}
            dataSource={query.data?.data || []}
            loading={query.isFetching}
            pagination={false}
            scroll={{ x: 1100 }}
            locale={{ emptyText: 'No recorded devices' }}
            expandable={{
              expandedRowRender: (row) => (
                <Descriptions
                  size="small"
                  column={1}
                  items={[
                    {
                      key: 'device',
                      label: 'Device ID',
                      children: row.device_id || 'Not reported',
                    },
                    {
                      key: 'version',
                      label: 'Client version',
                      children: emptyValue(row.client_version),
                    },
                    {
                      key: 'build',
                      label: 'Client build',
                      children: emptyValue(row.client_build),
                    },
                    {
                      key: 'agent',
                      label: 'User agent',
                      children: emptyValue(row.user_agent),
                    },
                  ]}
                />
              ),
            }}
          />
          <HistoryPagination
            page={page}
            perPage={perPage}
            hasMore={query.data?.has_more || false}
            loading={query.isFetching}
            onPage={setPage}
            onSize={(size) => {
              setPerPage(size);
              setPage(1);
            }}
          />
        </>
      )}
    </Space>
  );
};
